'use client';

import diff from 'fast-diff';
import TriangleAlert from 'lucide-react/dist/esm/icons/triangle-alert';
import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useIsMobile } from '@/hooks/use-mobile';

interface Name {
  name: string;
  date: string | Date;
}

interface ItemNameChangedProps {
  names: Name[];
}

const diffColors: Record<number, string> = {
  1: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 line-through',
  '-1': 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300',
  0: '',
};

function NameHistoryTable({
  formattedNames,
}: {
  formattedNames: { name: [number, string][]; date: string }[];
}) {
  return (
    <div className="overflow-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="text-right font-semibold">שם המוצר</TableHead>
            <TableHead className="w-32 text-right font-semibold">תאריך שינוי</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {formattedNames.map(({ name, date }) => (
            <TableRow key={date} className="hover:bg-muted/50">
              <TableCell className="py-3 text-right font-medium">
                {name.map(([diffType, text]) => (
                  <span
                    key={`${diffType}-${text}`}
                    className={`${diffColors[diffType]} rounded px-1`}
                  >
                    {text}
                  </span>
                ))}
              </TableCell>
              <TableCell className="py-3 text-right text-muted-foreground">{date}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export default function ItemNameChanged({ names = [] }: ItemNameChangedProps) {
  const [open, setOpen] = useState(false);
  const isMobile = useIsMobile();

  const formattedNames = names
    .map((n, i, a) => {
      const nameDiff = i === 0 ? [[0, n.name] as [number, string]] : diff(n.name, a[i - 1].name);
      const date = new Date(n.date).toLocaleDateString('he-IL');
      return { name: nameDiff, date };
    })
    .reverse();

  if (names.length <= 1) {
    return null;
  }

  const title = 'היסטוריית שמות המוצר';
  const description =
    'שם המוצר לרוב ישתנה כדי לדייק בתיאור שלו, אך לעיתים יכול להיות בגלל שהוא השתנה למוצר אחר.';

  return (
    <>
      <button type="button" onClick={() => setOpen(true)}>
        <Badge className="cursor-pointer bg-green-600 hover:bg-green-700">
          שם המוצר השתנה
          <TriangleAlert className="ms-2 h-4 w-4" />
        </Badge>
      </button>

      {isMobile ? (
        <Drawer open={open} onOpenChange={setOpen}>
          <DrawerContent>
            <DrawerHeader className="text-start">
              <DrawerTitle>{title}</DrawerTitle>
              <DrawerDescription>{description}</DrawerDescription>
            </DrawerHeader>
            <div className="max-h-[60vh] overflow-auto px-4 pb-4">
              <NameHistoryTable formattedNames={formattedNames} />
            </div>
          </DrawerContent>
        </Drawer>
      ) : (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-h-[85vh] overflow-auto sm:max-w-fit">
            <DialogHeader>
              <DialogTitle className="text-right">{title}</DialogTitle>
              <DialogDescription className="text-right">{description}</DialogDescription>
            </DialogHeader>
            <NameHistoryTable formattedNames={formattedNames} />
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
